/**
 * Monitoring → Async Jobs: pickup and execution percentiles per job type.
 *
 * Pickup is queued → started (waiting for a slot); execution is started →
 * completed (doing the work). Splitting them is the point — a slow total means
 * something different depending on which half it lives in, and only pickup is
 * ours to fix by tuning. Caps live in Settings → Async Jobs.
 */

import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';

/** Seconds → a readable duration. Pickup is often sub-second, exec often minutes. */
function dur(s: number | null): string {
    if (s === null) return '—';
    if (s < 1) return '<1s';
    if (s < 90) return `${Math.round(s)}s`;
    return `${(s / 60).toFixed(1)}m`;
}

export function AsyncJobTimings({ window, autoRefresh }: { window: string; autoRefresh?: boolean }) {
    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['async-job-timings', window],
        queryFn: () => adminApi.getAsyncJobTimings(window),
        // "Running now" is live, so this table has to follow the page's
        // auto-refresh toggle — otherwise it freezes precisely when an admin
        // turned refresh on to watch something move.
        refetchInterval: autoRefresh ? 10_000 : false,
    });

    return (
        <div className="settings-section">
            <div className="section-header">
                <h3>Timings by job type</h3>
                <p className="section-description">
                    Pickup is the wait for a slot; execution is the work itself.
                    "Running now" is point-in-time, not window-scoped — a type can
                    show work in flight with no completed samples to time.
                </p>
            </div>

            {isLoading && <p className="muted">Loading timings…</p>}
            {isError && <p className="error-message">Failed: {(error as Error).message}</p>}

            {data && data.types.length === 0 && (
                <p className="muted">No jobs ran in this window.</p>
            )}

            {data && data.types.length > 0 && (
                <table className="dt dt--compact">
                    <thead>
                        <tr>
                            <th rowSpan={2}>Type</th>
                            <th rowSpan={2} style={{ width: 60 }}>Jobs</th>
                            {/* Point-in-time, unlike every column to its right. */}
                            <th rowSpan={2} style={{ width: 70 }}>Running now</th>
                            <th colSpan={3} style={{ textAlign: 'center' }}>Pickup (wait)</th>
                            <th colSpan={3} style={{ textAlign: 'center' }}>Execution</th>
                        </tr>
                        <tr>
                            <th style={{ width: 70 }}>p50</th>
                            <th style={{ width: 70 }}>p90</th>
                            <th style={{ width: 70 }}>max</th>
                            <th style={{ width: 70 }}>p50</th>
                            <th style={{ width: 70 }}>p90</th>
                            <th style={{ width: 70 }}>max</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.types.map(t => (
                            <tr key={t.type}>
                                <td><code>{t.type}</code></td>
                                <td className="muted">{t.pickup.n}</td>
                                <td className={t.runningNow > 0 ? undefined : 'muted'}
                                    style={{ fontWeight: t.runningNow > 0 ? 700 : undefined }}>
                                    {t.runningNow}
                                </td>
                                <td>{dur(t.pickup.p50)}</td>
                                {/* The one worth alerting on — a slot should be found fast. */}
                                <td style={{ fontWeight: (t.pickup.p90 ?? 0) > 30 ? 700 : undefined }}>
                                    {dur(t.pickup.p90)}
                                </td>
                                <td>{dur(t.pickup.max)}</td>
                                <td className="muted">{dur(t.exec.p50)}</td>
                                <td className="muted">{dur(t.exec.p90)}</td>
                                <td className="muted">{dur(t.exec.max)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}


export default AsyncJobTimings;
